import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { IonPage } from "@ionic/react";
import { Button, Typography, Row, Col, Form, Breadcrumb, Space, Spin, Input, notification, Result } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";

//
import RequestMeta from "../../../components/RequestMeta";
import SectionHeader from "../../../components/GetStarted/SectionHeader";

//
import useAuthStore from "../../../states/useAuth";
import useRequest from "../../../hooks/useRequest";
import PageHeader from "../../../components/PageHeader";
import Status from "../../../components/Table/RequestStatus";
import useRequestStore from "../../../states/useRequest";
import RequestDetailEdit from "../../../components/ShopRequest/Edit";
import { showErrMsg } from "../../../utils/error";
import RequestDetailView from "../../../components/ShopRequest/View";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SingleShopRequest = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams(); // Get the :id parameter from the URL
  const { resetRequest } = useRequestStore();
  const { getSingleShopRequest, updateShopRequest, createShop } = useRequest();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  const [shopForm] = Form.useForm();
  const [brandForm] = Form.useForm();
  const [companyForm] = Form.useForm();

  const [originalRequest, setOriginalRequest] = useState();
  const [request, setRequest] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [isFetchingSingleRequest, setIsFetchingSingleRequest] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  const isActionBtnDisabled =
    id == "new" ? !c.hasDiff(originalRequest, request) : !originalRequest || !c.hasDiff(originalRequest, request);

  const parseFormData = async () => {
    let { name, logo, category, address, description, phone, email, website, gallery } = request;
    log.debug({ name, logo, category, address, description, phone, email, website, gallery });

    return { name, logo, category, address, description, phone, email, website, gallery };
  };

  const updateRequestData = (newData) => {
    setRequest((prev) => {
      const updated = _.merge({}, prev, { info: newData });

      // Explicitly overwrite certain fields instead of merging
      if ("shop" in newData && "gallery" in newData.shop) {
        updated.info.shop.gallery = newData.shop.gallery;
      }
      if ("brand" in newData && "category" in newData.brand) {
        updated.info.brand.category = newData.brand.category;
      }

      return updated;
    });
  };

  const getSingleRequestData = async () => {
    //setActiveTab
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    // const queryParams = c.parseQueryToJSON(queryURL);
    // const { tab } = queryParams;
    // log.debug(tab);
    // setActiveTab(tab);

    //
    log.debug(id);
    if (id == "new") {
      return;
    }

    let result;

    try {
      setIsFetchingSingleRequest(true);
      result = await getSingleShopRequest(id);
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    }

    setIsFetchingSingleRequest(false);
    log.debug(result);

    setOriginalRequest(result);
    setRequest(result);
  };

  // Function to handle updating both the form and state
  const submitRequest = async () => {
    //check form
    try {
      if (id != "new") {
        await companyForm.validateFields();
        await brandForm.validateFields();
      }

      await shopForm.validateFields();
    } catch (error) {
      log.error(error);
      // notification.warning({
      //   description: (
      //     <Text style={{ fontSize: "1rem" }}>Some of the input fields are invalid. Please review and save again</Text>
      //   ),
      // });
      return c.message({
        type: "error",
        content: "Some of the input fields are invalid. Please review and save again",
      });
    }

    // Get the diff between originalRequest and request
    const diff = c.getObjectDiff(originalRequest, request);
    console.log("Changed fields:", diff);

    if (!diff.info) {
      return c.message({ type: "error", content: "no information changed" });
    }

    // axios
    try {
      setIsLoadingSubmit(true);

      let response;
      if (id === "new") {
        response = await createShop({ shop: diff.info.shop });
      } else {
        response = await updateShopRequest(request.id, { info: diff.info, status: "pending" });
      }

      // Update data
      setOriginalRequest(response);
      setRequest(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      //redirect
      if (id === "new") {
        history.replace(`/merchantUser/shop/${response.id}`);
      }
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetRequest(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getSingleRequestData();
  }, [id]);

  return (
    <div
      style={{
        margin: "0 auto",
        // background: "var(--ion-color-step-50)",
      }}
    >
      <Spin
        spinning={isFetchingSingleRequest}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/shop");
                    }}
                  >
                    Shop List
                  </a>
                ),
              },
              {
                title: <a>Shop</a>,
              },
            ]}
          />

          <PageHeader
            title={
              <Space>
                {id != "new" ? "Request Detail" : "Create Shop"}
                {id != "new" && request ? <Status status={request.status} /> : null}
              </Space>
            }
            isSticky
            actionGroup={
              request?.status == "approved" ? null : (
                <Space>
                  <Button
                    size="large"
                    disabled={isActionBtnDisabled}
                    loading={isLoadingSubmit}
                    color="default"
                    variant="filled"
                    style={{ marginRight: "1.5rem", width: "7.5rem" }}
                    onClick={async () => {
                      //reset
                      setRequest(originalRequest);
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    size="large"
                    type="primary"
                    disabled={isActionBtnDisabled}
                    loading={isLoadingSubmit}
                    style={{ marginRight: "1.5rem", width: "7.5rem" }}
                    onClick={async () => {
                      // let data = await parseFormData();
                      submitRequest();
                    }}
                  >
                    Submit
                  </Button>
                </Space>
              )
            }
            onBack={() => {
              history.goBack();
            }}
          />

          {isFetchingSingleRequest ? null : (
            <>
              {id != "new" && request ? <RequestMeta request={request} /> : null}

              <Row
                style={{
                  background: "var(--ion-color-step-50)",
                  paddingTop: "2rem",
                }}
              >
                {request?.status == "approved" ? (
                  <RequestDetailView
                    state={{
                      data: request,
                      onChange: updateRequestData,
                    }}
                    shopForm={shopForm}
                    brandForm={brandForm}
                    companyForm={companyForm}
                  />
                ) : (
                  <RequestDetailEdit
                    state={{
                      data: request?.info?.shop,
                      onChange: updateRequestData,
                      form: shopForm,
                    }}
                    brand={request?.info?.brand}
                  />
                )}
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SingleShopRequest;
